import { createGlobalStyle } from 'styled-components';
import { primitiveColors } from './template';

/**
 * Глобальные стили **документа** — применяются ко всей странице, включая
 * легаси-сайт, в который встроены React-острова.
 *
 * Раздел разделён на ДВА `createGlobalStyle`-экспорта:
 *
 *  1. `GlobalStyle` (default) — **theme-stable** правила, которые НЕ
 *     перерендериваются при смене темы. Их можно безопасно подключать
 *     в любом бандле, включая islands-бандл, встроенный в легаси-сайт:
 *     при переключении темы фон/цвет легаси не дёргается.
 *
 *  2. `BodyThemeStyles` — **theme-driven body**: только `body { background;
 *     color }` из текущей темы. При смене темы React-стороной этот
 *     компонент перерендерится → меняется фон/цвет легаси-сайта тоже.
 *     **Сейчас НЕ подключён** ни в `AppIslands.tsx`, ни в `App.tsx`,
 *     чтобы смена темы из React-острова (ThemeSwitcher) не вмешивалась
 *     в легаси. Подключается в Storybook/тестах (там нет легаси, нужен
 *     визуальный фон).
 *
 *     Когда захочется распространить смену темы и на легаси — добавить
 *     `<BodyThemeStyles />` в `AppIslands.tsx` (и/или `App.tsx`),
 *     импорт из `@/styles/global`. Это **одна строка** — см. коммент в
 *     `AppIslands.tsx`.
 *
 * Element-level reset'ы (`input`, `button`, `form`, `*`, margin/padding
 * на типографии и т.п.) живут в `src/styles/IslandScope.tsx` через
 * `InsideShadowReset` (для shadow) и `IslandScopeReset` (для
 * Storybook/тестов). См. правило `.cursor/rules/island-scoping.mdc`.
 */
const GlobalStyle = createGlobalStyle`

    :root {
        font-synthesis: none;
        text-rendering: optimizeLegibility;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        -webkit-text-size-adjust: 100%;
    }

    html {
        font-size: 8px; /* база для rem-юнитов React-стайлгайда */
        cursor: default;
        -moz-tab-size: 4;
        -o-tab-size: 4;
        tab-size: 4;
        -webkit-tap-highlight-color: transparent;
        -ms-text-size-adjust: 100%;
        -webkit-text-size-adjust: 100%;
        word-break: break-word;
    }

    /*
     * body здесь — только theme-STABLE правила. background и color
     * вынесены в BodyThemeStyles ниже, чтобы смена темы не дёргала
     * фон легаси-сайта.
     */
    body {
        margin: 0;
        padding: 0;
        font-family: 'Roboto', sans-serif;
        font-size: 2rem;
        font-weight: 400;
        /* 1.5 — базовое отношение для латиницы/кириллицы; --lh-base поднимает
           его для «высоких» письменностей (см. html[lang="hi"] в main.css). */
        line-height: var(--lh-base, 1.5);
        min-height: 100vh;
        scroll-behavior: smooth;
        text-rendering: optimizeSpeed;
    }

    /*
     * Глушим все transitions на один кадр в момент смены темы, чтобы
     * переписанные styled-components-классы не анимировали background/
     * box-shadow/padding и не давали «дёрганья» (особенно заметно на
     * страницах с большим количеством стилизованных кнопок — например
     * MyWidgets с CounterAccordion + RemovableItem). Атрибут
     * проставляется и снимается в CustomThemeProvider.
     */
    html[data-theme-changing],
    html[data-theme-changing] *,
    html[data-theme-changing] *::before,
    html[data-theme-changing] *::after {
        transition: none !important;
        animation-duration: 0.001ms !important;
    }

    @media screen and (-ms-high-contrast: active) {
        border: 2px solid currentcolor;
    }

    /* Remove all animations and transitions for people that prefer not to see them */
    @media (prefers-reduced-motion: reduce) {
        * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
            scroll-behavior: auto !important;
        }
    }

    ::-webkit-scrollbar {
        width: 6px;
    }

    ::-webkit-scrollbar:horizontal {
        height: 0.5rem;
    }

    ::-webkit-scrollbar-track {
        background: transparent !important;
        border-radius: 10px;
        box-shadow: none;
    }

    ::-webkit-scrollbar-thumb {
        background: ${primitiveColors.gray[400]};
        border-radius: 10px;
    }

    ::-webkit-scrollbar-thumb:hover {
        background: ${primitiveColors.accent[500]};
    }
`;

export default GlobalStyle;

/**
 * Theme-driven body styles — те правила `<body>`, которые зависят от
 * текущей темы. Вынесены в отдельный `createGlobalStyle`, чтобы их
 * можно было НЕ подключать в островном бандле и не вмешиваться в
 * легаси-сайт при переключении темы из React-острова.
 *
 * Сейчас подключается только в Storybook и `renderWithProviders`, где
 * нет легаси и фон нужен для визуальной проверки сторис/тестов.
 *
 * Когда будете расширять смену темы на легаси-сайт — добавьте
 * `<BodyThemeStyles />` рядом с `<GlobalStyle />` в `AppIslands.tsx`
 * (и/или `App.tsx`). Импорт — из этого же файла.
 */
export const BodyThemeStyles = createGlobalStyle`
    body {
        background: ${({ theme }) => theme.semantic.surface.background ?? primitiveColors.gray[100]};
        color: ${({ theme }) => theme.semantic.text.primary ?? primitiveColors.gray[500]};
    }
`;
